import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft, FloppyDisk, FilePdf, Copy, UploadSimple, FileXls, ListChecks,
  Plus, X, Lightning, Flame, Lightning as Bolt,
  ChartPieSlice, FileArrowDown, ClockCounterClockwise, Trash, DownloadSimple,
  CheckCircle, Warning, Paperclip,
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TIPOLOGIA_LABELS = { luce: 'Luce', gas: 'Gas', dual: 'Dual' };
const TIPOLOGIA_ICONS = { luce: Lightning, gas: Flame, dual: Bolt };
const PREZZO_LABELS = { fisso: 'Fisso', indicizzato: 'Indicizzato' };
const CATEGORIA_LABELS = { domestico: 'Domestico', altri_usi: 'Altri Usi' };

const FLAG_LABELS = {
  sepa: 'Sconto SEPA (SDD)',
  online: 'Sconto Online',
  costo_invio_documento: 'Costo invio documento',
  euro_bolletta: 'Euro/Bolletta',
  specifico: 'Sconto specifico',
  vendita_senza_spread: 'Vendita senza spread',
};

const initOffer = () => ({
  nome_offerta: '',
  codice_interno: '',
  codice_sii: '',
  tipologia: 'luce',
  categoria: 'domestico',
  tipo_prezzo: 'fisso',
  indice: 'PUN_MEDIO',
  validita_dal: '',
  validita_al: '',
  durata_offerta_mesi: 12,
  fasce: [
    { codice: 'F1', prezzo_fisso: '', spread: '', pun_value: '' },
    { codice: 'F2', prezzo_fisso: '', spread: '', pun_value: '' },
    { codice: 'F3', prezzo_fisso: '', spread: '', pun_value: '' },
  ],
  perdite_rete_pct: 10,
  pcv_qvd_eur_mese: 0,
  costo_fisso_anno: 0,
  corrispettivo_non_auto: 0,
  sconti: [],
  flags_sconto: { sepa: false, online: false, costo_invio_documento: false, euro_bolletta: false, specifico: false, vendita_senza_spread: false },
  tipo_dispacciamento: 'TIDE_539_2024',
  capacity_market: 'TERNA',
  componenti_impresa: [],
  componenti_regolate: [],
  componenti_regolate_specifiche: [],
  esclusioni_componenti: [],
  sconti_componenti: [],
  sconti_materia: [],
  metodi_pagamento: [],
  metodi_spedizione: [],
  metodi_attivazione: [],
  canali_acquisizione: [],
  regioni_vendibilita: [],
  province_vendibilita: [],
  comuni_vendibilita: [],
  condizioni_offerta: [],
  testi_aggiuntivi: [],
  allegati_aggiuntivi: [],
  vendibile_online: false,
  visibile_online: false,
  offerta_piacet: false,
  offerta_vulnerabili: false,
  margine_commerciale: { atteso_eur_mwh: 0, minimo_eur_mwh: 0, note: '' },
  stile_aggiuntivo: [],
  allegati: [],
  custom_cte_pdf: null,
  history: [],
  cte_template: 'CTE_DOMESTICO.html',
  pun_reference: { month: '', F1: '', F2: '', F3: '', max_value: '', max_month: '' },
  attiva: true,
});

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
              'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export default function OfferEditorPage() {
  const navigate = useNavigate();
  const { offerId } = useParams();
  const isNew = offerId === 'new';

  const [form, setForm] = useState(initOffer());
  const [templates, setTemplates] = useState([]);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [incidenza, setIncidenza] = useState(null);
  const [uploadCteFile, setUploadCteFile] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, optRes] = await Promise.all([
        fetch(`${API}/api/offer-templates`, { credentials: 'include' }),
        fetch(`${API}/api/offers/options/all`, { credentials: 'include' }),
      ]);
      if (tRes.ok) setTemplates(await tRes.json());
      if (optRes.ok) setOptions(await optRes.json());
      if (!isNew) {
        const oRes = await fetch(`${API}/api/offers/${offerId}`, { credentials: 'include' });
        if (oRes.ok) {
          const o = await oRes.json();
          setForm({ ...initOffer(), ...o,
            pun_reference: o.pun_reference || initOffer().pun_reference,
            flags_sconto: o.flags_sconto || initOffer().flags_sconto,
            margine_commerciale: o.margine_commerciale || initOffer().margine_commerciale,
            fasce: o.fasce && o.fasce.length ? o.fasce : initOffer().fasce,
          });
        } else {
          toast.error('CTE non trovata');
          navigate('/offers');
        }
      }
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [offerId, isNew, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    if (!form.nome_offerta.trim()) return toast.error('Nome CTE obbligatorio');
    setSaving(true);
    try {
      const url = isNew ? `${API}/api/offers` : `${API}/api/offers/${offerId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isNew ? 'CTE creata' : 'Salvata');
        if (isNew && data.id) navigate(`/offers/${data.id}/edit`);
      } else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore'); }
    finally { setSaving(false); }
  };

  const action = async (path, label) => {
    if (isNew) return toast.error('Salva prima la CTE');
    try {
      const res = await fetch(`${API}/api/offers/${offerId}/${path}`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { const d = await res.json(); return toast.error(d.detail || 'Errore'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success(`${label} generato`);
    } catch (e) { toast.error('Errore'); }
  };

  const downloadGet = async (path, label) => {
    if (isNew) return toast.error('Salva prima la CTE');
    try {
      const res = await fetch(`${API}/api/offers/${offerId}/${path}`, { credentials: 'include' });
      if (!res.ok) { const d = await res.json(); return toast.error(d.detail || 'Errore'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `${form.nome_offerta || 'cte'}_${path.replace('/', '_')}.xlsx`;
      a.click();
      toast.success(`${label} scaricato`);
    } catch (e) { toast.error('Errore'); }
  };

  const uploadCustomCte = async (file) => {
    if (isNew) return toast.error('Salva prima la CTE');
    if (!file.name.toLowerCase().endsWith('.pdf')) return toast.error('Solo PDF');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/offers/${offerId}/upload-cte`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    const data = await res.json();
    if (res.ok) {
      toast.success('CTE custom caricata');
      setForm({ ...form, custom_cte_pdf: data });
      fetchData();
    } else toast.error(data.detail || 'Errore upload');
  };

  const removeCustomCte = async () => {
    if (!window.confirm('Rimuovere CTE custom? Tornerà attivo il template generato.')) return;
    const res = await fetch(`${API}/api/offers/${offerId}/custom-cte`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) { toast.success('CTE rimossa'); fetchData(); }
  };

  const showIncidenza = async () => {
    if (isNew) return toast.error('Salva prima la CTE');
    try {
      const res = await fetch(`${API}/api/offers/${offerId}/incidenza`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setIncidenza(data);
      else toast.error(data.detail || 'Errore');
    } catch (e) { toast.error('Errore'); }
  };

  const uploadAttachment = async (file, label) => {
    if (isNew) return toast.error('Salva prima la CTE');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('label', label || file.name);
    fd.append('ordinamento', String((form.allegati || []).length));
    const res = await fetch(`${API}/api/offers/${offerId}/attachments`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    const data = await res.json();
    if (res.ok) { toast.success('Allegato caricato'); fetchData(); }
    else toast.error(data.detail || 'Errore');
  };

  const removeAttachment = async (aid) => {
    if (!window.confirm('Eliminare allegato?')) return;
    const res = await fetch(`${API}/api/offers/${offerId}/attachments/${aid}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) { toast.success('Eliminato'); fetchData(); }
  };

  const duplicate = async () => {
    if (isNew) return toast.error('Salva prima la CTE');
    try {
      const res = await fetch(`${API}/api/offers/${offerId}/duplicate`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data.id) {
        toast.success('Duplicata');
        navigate(`/offers/${data.id}/edit`);
      }
    } catch (e) { toast.error('Errore'); }
  };

  const updateField = (k, v) => setForm({ ...form, [k]: v });
  const updateFascia = (i, k, v) => {
    const next = [...form.fasce];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, fasce: next });
  };
  const updateFlag = (k, v) => setForm({ ...form, flags_sconto: { ...form.flags_sconto, [k]: v } });
  const updatePun = (k, v) => setForm({ ...form, pun_reference: { ...form.pun_reference, [k]: v } });
  const updateMargine = (k, v) => setForm({ ...form, margine_commerciale: { ...form.margine_commerciale, [k]: v } });

  const addRow = (key, row) => setForm({ ...form, [key]: [...(form[key] || []), row] });
  const updateRow = (key, i, k, v) => {
    const next = [...(form[key] || [])];
    next[i] = { ...next[i], [k]: v };
    setForm({ ...form, [key]: next });
  };
  const removeRow = (key, i) => setForm({ ...form, [key]: form[key].filter((_, idx) => idx !== i) });

  const toggleMulti = (key, val) => {
    const list = form[key] || [];
    const next = list.includes(val) ? list.filter(x => x !== val) : [...list, val];
    setForm({ ...form, [key]: next });
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  const Icon = TIPOLOGIA_ICONS[form.tipologia] || Lightning;

  // Compute "vendibile oggi" client-side
  const todayISO = new Date().toISOString().slice(0, 10);
  const toISO = (s) => {
    if (!s) return '';
    if (s.length === 10 && s[2] === '/') return `${s.slice(6, 10)}-${s.slice(3, 5)}-${s.slice(0, 2)}`;
    return s;
  };
  const dalISO = toISO(form.validita_dal);
  const alISO = toISO(form.validita_al);
  const vendibileOggi = !isNew && form.attiva &&
    (!dalISO || dalISO <= todayISO) && (!alISO || todayISO <= alISO);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="offer-editor">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/offers')} data-testid="back-to-offers">
          <ArrowLeft size={18} className="mr-1" /> CTE
        </Button>
        <div className="text-slate-300">/</div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Icon size={22} weight="duotone" className="text-orange-500" />
          {isNew ? 'Nuova CTE' : (form.nome_offerta || 'CTE')}
          {!isNew && form.codice_sii && (
            <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-1 rounded">{form.codice_sii}</span>
          )}
          {form.attiva ? (
            <Badge className="bg-green-100 text-green-700">Attiva</Badge>
          ) : <Badge variant="secondary">Inattiva</Badge>}
          {!isNew && vendibileOggi && (
            <Badge className="bg-blue-100 text-blue-700" data-testid="vendibile-oggi-badge"><CheckCircle size={12} className="inline mr-1" />Vendibile oggi</Badge>
          )}
        </h1>
        {form.updated_by && (
          <div className="ml-auto text-xs text-slate-400">
            Ultima modifica: {form.updated_by} · {(form.updated_at || '').slice(0, 10)}
          </div>
        )}
      </div>

      {/* TOOLBAR */}
      <div className="flex flex-wrap gap-2 mb-5 sticky top-0 bg-slate-50/95 backdrop-blur z-10 py-2 border-b">
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-offer">
          <FloppyDisk size={16} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <Button onClick={() => action('generate-cte', 'CTE')} variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50" data-testid="generate-cte">
          <FilePdf size={16} className="mr-1" /> Genera CTE
        </Button>
        <Button onClick={() => action('scheda-sintetica', 'Scheda sintetica')} variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50" data-testid="scheda-sintetica">
          <ListChecks size={16} className="mr-1" /> Scheda Sintetica
        </Button>
        <Button onClick={duplicate} variant="outline"
                className="border-orange-200 text-orange-600 hover:bg-orange-50" data-testid="duplicate-offer">
          <Copy size={16} className="mr-1" /> Duplica
        </Button>
        <Button onClick={() => setShowUploadDialog(true)} variant="outline"
                className="border-purple-200 text-purple-600 hover:bg-purple-50" data-testid="upload-cte-btn"
                disabled={isNew}>
          <UploadSimple size={16} className="mr-1" /> Upload PDF
        </Button>
        <Button onClick={() => downloadGet('calcolo-xls', 'Tariffe Excel')} variant="outline"
                className="border-yellow-300 text-yellow-700 hover:bg-yellow-50" data-testid="download-xls"
                disabled={isNew}>
          <FileXls size={16} className="mr-1" /> Scarica XLS
        </Button>
        <Button onClick={showIncidenza} variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50" data-testid="incidenza-btn"
                disabled={isNew}>
          <ChartPieSlice size={16} className="mr-1" /> Incidenza %
        </Button>
        {form.custom_cte_pdf?.filename && (
          <div className="ml-auto flex items-center gap-2 text-xs bg-purple-50 border border-purple-200 px-2 py-1 rounded">
            <Paperclip size={14} className="text-purple-600" />
            <span className="text-purple-700">CTE custom: {form.custom_cte_pdf.filename}</span>
            <button onClick={removeCustomCte} className="text-red-600 hover:bg-red-50 p-1 rounded">
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      {/* SECTIONS */}
      <Accordion type="multiple" defaultValue={['identita', 'tipologia', 'prezzi']} className="space-y-2">

        {/* 1. IDENTITÀ */}
        <Section value="identita" title="Identità">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome CTE *">
              <Input value={form.nome_offerta} onChange={(e) => updateField('nome_offerta', e.target.value)}
                     data-testid="offer-name" placeholder="Es: EGO INDEX HOC INDUSTRIAL 2026" />
            </Field>
            <Field label="Codice interno">
              <Input value={form.codice_interno} onChange={(e) => updateField('codice_interno', e.target.value)}
                     placeholder="Es: RELAX_LUCE_2026" />
            </Field>
            <Field label="Codice SII (32 char)" hint="Auto-generato se vuoto">
              <Input value={form.codice_sii} onChange={(e) => updateField('codice_sii', e.target.value)}
                     maxLength={32} className="font-mono text-xs" />
            </Field>
            <Field label="Stato">
              <label className="flex items-center gap-2 mt-2">
                <Switch checked={form.attiva} onCheckedChange={(v) => updateField('attiva', v)} />
                <span className="text-sm">{form.attiva ? 'Attiva' : 'Inattiva'}</span>
              </label>
            </Field>
          </div>
        </Section>

        {/* 2. TIPOLOGIA */}
        <Section value="tipologia" title="Tipologia & Validità">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Tipologia">
              <Select value={form.tipologia} onValueChange={(v) => updateField('tipologia', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options.tipologie || []).map(t => <SelectItem key={t} value={t}>{TIPOLOGIA_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Categoria">
              <Select value={form.categoria} onValueChange={(v) => updateField('categoria', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options.categorie || []).map(c => <SelectItem key={c} value={c}>{CATEGORIA_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tipo prezzo">
              <Select value={form.tipo_prezzo} onValueChange={(v) => updateField('tipo_prezzo', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options.tipi_prezzo || []).map(t => <SelectItem key={t} value={t}>{PREZZO_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {form.tipo_prezzo === 'indicizzato' && (
              <Field label="Indice">
                <Select value={form.indice} onValueChange={(v) => updateField('indice', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.indici || []).map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Validità dal">
              <Input value={form.validita_dal} onChange={(e) => updateField('validita_dal', e.target.value)}
                     placeholder="01/01/2026" />
            </Field>
            <Field label="Validità al">
              <Input value={form.validita_al} onChange={(e) => updateField('validita_al', e.target.value)}
                     placeholder="31/12/2026" />
            </Field>
            <Field label="Durata (mesi)">
              <Input type="number" value={form.durata_offerta_mesi}
                     onChange={(e) => updateField('durata_offerta_mesi', parseInt(e.target.value) || 12)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t">
            <ToggleField label="Vendibile online" v={form.vendibile_online} onChange={(v) => updateField('vendibile_online', v)} />
            <ToggleField label="Visibile online" v={form.visibile_online} onChange={(v) => updateField('visibile_online', v)} />
            <ToggleField label="CTE PLACET" v={form.offerta_piacet} onChange={(v) => updateField('offerta_piacet', v)} />
            <ToggleField label="Per vulnerabili" v={form.offerta_vulnerabili} onChange={(v) => updateField('offerta_vulnerabili', v)} />
          </div>
        </Section>

        {/* 3. PREZZI PER FASCIA */}
        <Section value="prezzi" title={`Prezzi per fascia (${form.tipo_prezzo})`}>
          <table className="w-full text-sm border rounded">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-2 w-20">Fascia</th>
                {form.tipo_prezzo === 'fisso' ? (
                  <th className="text-left p-2">Prezzo fisso (€/kWh)</th>
                ) : (
                  <>
                    <th className="text-left p-2">Spread (€/kWh)</th>
                    <th className="text-left p-2">PUN snapshot</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {form.fasce.map((f, i) => (
                <tr key={i} className="border-t">
                  <td className="p-2 font-mono font-bold">{f.codice}</td>
                  {form.tipo_prezzo === 'fisso' ? (
                    <td className="p-2">
                      <Input value={f.prezzo_fisso || ''} onChange={(e) => updateFascia(i, 'prezzo_fisso', e.target.value)}
                             placeholder="0,12345" data-testid={`fascia-${f.codice}-fisso`} />
                    </td>
                  ) : (
                    <>
                      <td className="p-2">
                        <Input value={f.spread || ''} onChange={(e) => updateFascia(i, 'spread', e.target.value)}
                               placeholder="0,00880" data-testid={`fascia-${f.codice}-spread`} />
                      </td>
                      <td className="p-2">
                        <Input value={f.pun_value || ''} onChange={(e) => updateFascia(i, 'pun_value', e.target.value)}
                               placeholder="0,13500" />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Field label="Perdite rete (% BT/MT)">
              <Input type="number" step="0.1" value={form.perdite_rete_pct}
                     onChange={(e) => updateField('perdite_rete_pct', parseFloat(e.target.value) || 0)} />
            </Field>
            {form.tipo_prezzo === 'indicizzato' && (
              <>
                <Field label="Mese rif. PUN">
                  <Input value={form.pun_reference?.month || ''} onChange={(e) => updatePun('month', e.target.value)}
                         placeholder="DIC 2025" />
                </Field>
                <Field label="PUN max storico">
                  <Input value={form.pun_reference?.max_value || ''} onChange={(e) => updatePun('max_value', e.target.value)}
                         placeholder="0,15420" />
                </Field>
              </>
            )}
          </div>
        </Section>

        {/* 4. COSTI FISSI */}
        <Section value="costi" title="Costi fissi e PCV/QVD">
          <div className="grid grid-cols-3 gap-3">
            <Field label="PCV/QVD (€/mese)">
              <Input type="number" step="0.01" value={form.pcv_qvd_eur_mese}
                     onChange={(e) => updateField('pcv_qvd_eur_mese', parseFloat(e.target.value) || 0)}
                     data-testid="pcv-qvd" />
            </Field>
            <Field label="Costo fisso anno (€)">
              <Input type="number" step="0.01" value={form.costo_fisso_anno}
                     onChange={(e) => updateField('costo_fisso_anno', parseFloat(e.target.value) || 0)}
                     data-testid="costo-fisso-anno" />
            </Field>
            <Field label="Pagamento non auto (€/kWh)">
              <Input type="number" step="0.0001" value={form.corrispettivo_non_auto}
                     onChange={(e) => updateField('corrispettivo_non_auto', parseFloat(e.target.value) || 0)} />
            </Field>
          </div>
        </Section>

        {/* 5. FLAG SCONTI */}
        <Section value="flags" title="Flag sconti applicati">
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(FLAG_LABELS).map(([k, label]) => (
              <ToggleField key={k} label={label}
                v={form.flags_sconto?.[k] || false}
                onChange={(v) => updateFlag(k, v)} />
            ))}
          </div>
        </Section>

        {/* 6. SCONTI MATERIA / PER FASCIA */}
        <Section value="sconti" title="Sconti / Bonus">
          <RowEditor
            rows={form.sconti}
            columns={[
              { key: 'tipo', label: 'Tipo', type: 'select', options: [
                { v: 'sdd', l: 'SDD' }, { v: 'bolletta_web', l: 'Bolletta Web' },
                { v: 'dual', l: 'Dual' }, { v: 'benvenuto', l: 'Benvenuto' }, { v: 'altro', l: 'Altro' },
              ], width: 'col-span-3' },
              { key: 'descrizione', label: 'Descrizione', type: 'text', width: 'col-span-6' },
              { key: 'valore_eur_anno', label: '€/anno', type: 'number', width: 'col-span-2' },
            ]}
            onAdd={() => addRow('sconti', { tipo: 'sdd', descrizione: '', valore_eur_anno: 0 })}
            onUpdate={(i, k, v) => updateRow('sconti', i, k, v)}
            onRemove={(i) => removeRow('sconti', i)} />
        </Section>

        {/* 7. SCONTI MATERIA */}
        <Section value="sconti-materia" title="Sconti Materia (per fascia/mese solare)">
          <RowEditor
            rows={form.sconti_materia}
            columns={[
              { key: 'voce', label: 'Voce', type: 'text', width: 'col-span-4' },
              { key: 'importo', label: 'Importo', type: 'text', width: 'col-span-3' },
              { key: 'aliquota', label: 'Unità', type: 'select', options: [
                { v: '€/kWh', l: '€/kWh' }, { v: '€/Smc', l: '€/Smc' }, { v: '€/mese', l: '€/mese' }, { v: '%', l: '%' },
              ], width: 'col-span-2' },
              { key: 'dal_mese', label: 'Dal mese', type: 'select',
                options: MESI.map(m => ({ v: m, l: m })), width: 'col-span-2' },
            ]}
            onAdd={() => addRow('sconti_materia', { voce: '', importo: '', aliquota: '€/kWh', dal_mese: 'Gennaio' })}
            onUpdate={(i, k, v) => updateRow('sconti_materia', i, k, v)}
            onRemove={(i) => removeRow('sconti_materia', i)} />
        </Section>

        {/* 8. COMPONENTI IMPRESA */}
        <Section value="comp-impresa" title="Componenti Impresa">
          <p className="text-xs text-slate-500 mb-2">Sbilanciamento, Capacity Market, Dispacciamento, OVD</p>
          <RowEditor
            rows={form.componenti_impresa}
            columns={[
              { key: 'codice', label: 'Codice', type: 'select',
                options: (options.componenti_impresa_voci || []).map(c => ({ v: c.codice, l: `${c.codice} – ${c.nome}` })),
                width: 'col-span-4' },
              { key: 'importo', label: 'Importo', type: 'text', width: 'col-span-3' },
              { key: 'aliquota', label: 'Unità', type: 'select', options: [
                { v: '€/kWh', l: '€/kWh' }, { v: '€/Smc', l: '€/Smc' }, { v: '€/mese', l: '€/mese' }, { v: '%', l: '%' },
              ], width: 'col-span-2' },
              { key: 'dal_mese', label: 'Dal mese', type: 'select',
                options: MESI.map(m => ({ v: m, l: m })), width: 'col-span-2' },
            ]}
            onAdd={() => addRow('componenti_impresa', { codice: 'SBILANC', importo: '0,00650000', aliquota: '€/kWh', dal_mese: 'Gennaio' })}
            onUpdate={(i, k, v) => updateRow('componenti_impresa', i, k, v)}
            onRemove={(i) => removeRow('componenti_impresa', i)} />
        </Section>

        {/* 9. COMPONENTI REGOLATE */}
        <Section value="comp-reg" title="Componenti Regolate">
          <p className="text-xs text-slate-500 mb-2">PCV, Oneri di Sistema, Trasporto, Accise, IVA, Canone RAI</p>
          <RowEditor
            rows={form.componenti_regolate}
            columns={[
              { key: 'codice', label: 'Voce', type: 'select',
                options: (options.componenti_regolate_voci || []).map(c => ({ v: c.codice, l: `${c.codice} – ${c.nome}` })),
                width: 'col-span-6' },
              { key: 'importo_in_vigore', label: 'Importo in vigore', type: 'text', width: 'col-span-5' },
            ]}
            onAdd={() => addRow('componenti_regolate', { codice: 'PCV', importo_in_vigore: '' })}
            onUpdate={(i, k, v) => updateRow('componenti_regolate', i, k, v)}
            onRemove={(i) => removeRow('componenti_regolate', i)} />
        </Section>

        {/* 9-bis. COMPONENTI REGOLATE SPECIFICHE */}
        <Section value="comp-reg-spec" title="Componenti Regolate Specifiche">
          <p className="text-xs text-slate-500 mb-2">Voci regolate aggiuntive specifiche per questa CTE</p>
          <RowEditor
            rows={form.componenti_regolate_specifiche}
            columns={[
              { key: 'codice', label: 'Codice', type: 'text', width: 'col-span-3' },
              { key: 'nome', label: 'Nome / Descrizione', type: 'text', width: 'col-span-8' },
            ]}
            onAdd={() => addRow('componenti_regolate_specifiche', { codice: '', nome: '' })}
            onUpdate={(i, k, v) => updateRow('componenti_regolate_specifiche', i, k, v)}
            onRemove={(i) => removeRow('componenti_regolate_specifiche', i)} />
        </Section>

        {/* 9-ter. SCONTI COMPONENTI */}
        <Section value="sconti-comp" title="Sconti su Componenti">
          <p className="text-xs text-slate-500 mb-2">Sconti specifici applicati a singole componenti (impresa o regolate)</p>
          <RowEditor
            rows={form.sconti_componenti}
            columns={[
              { key: 'voce', label: 'Voce', type: 'text', width: 'col-span-4' },
              { key: 'importo', label: 'Importo', type: 'text', width: 'col-span-3' },
              { key: 'aliquota', label: 'Unità', type: 'select', options: [
                { v: '€/kWh', l: '€/kWh' }, { v: '€/Smc', l: '€/Smc' }, { v: '€/mese', l: '€/mese' }, { v: '%', l: '%' },
              ], width: 'col-span-2' },
              { key: 'dal_mese', label: 'Dal mese', type: 'select',
                options: MESI.map(m => ({ v: m, l: m })), width: 'col-span-2' },
            ]}
            onAdd={() => addRow('sconti_componenti', { voce: '', importo: '', aliquota: '€/kWh', dal_mese: 'Gennaio' })}
            onUpdate={(i, k, v) => updateRow('sconti_componenti', i, k, v)}
            onRemove={(i) => removeRow('sconti_componenti', i)} />
        </Section>

        {/* 10. ESCLUSIONI COMPONENTI */}
        <Section value="esclusioni" title="Escludi Componenti">
          <RowEditor
            rows={form.esclusioni_componenti}
            columns={[
              { key: 'codice', label: 'Codice voce', type: 'text', width: 'col-span-4' },
              { key: 'descrizione', label: 'Motivo esclusione', type: 'text', width: 'col-span-6' },
              { key: 'visibile', label: 'Visibile', type: 'select',
                options: [{ v: 'true', l: 'Sì' }, { v: 'false', l: 'No' }], width: 'col-span-1' },
            ]}
            onAdd={() => addRow('esclusioni_componenti', { codice: '', descrizione: '', visibile: 'false' })}
            onUpdate={(i, k, v) => updateRow('esclusioni_componenti', i, k, v)}
            onRemove={(i) => removeRow('esclusioni_componenti', i)} />
        </Section>

        {/* 11. METODI PAGAMENTO/SPEDIZIONE/ATTIVAZIONE/CANALI */}
        <Section value="metodi" title="Metodi & Canali">
          <div className="grid grid-cols-2 gap-4">
            <MultiSelect label="Metodi di Pagamento"
              all={options.metodi_pagamento || []} selected={form.metodi_pagamento}
              onToggle={(v) => toggleMulti('metodi_pagamento', v)} />
            <MultiSelect label="Metodi di Spedizione"
              all={options.metodi_spedizione || []} selected={form.metodi_spedizione}
              onToggle={(v) => toggleMulti('metodi_spedizione', v)} />
            <MultiSelect label="Metodi di Attivazione"
              all={options.metodi_attivazione || []} selected={form.metodi_attivazione}
              onToggle={(v) => toggleMulti('metodi_attivazione', v)} />
            <MultiSelect label="Canali di Acquisizione"
              all={options.canali_acquisizione || []} selected={form.canali_acquisizione}
              onToggle={(v) => toggleMulti('canali_acquisizione', v)} />
          </div>
          <p className="text-xs text-slate-400 mt-2">Lascia vuoto per abilitare tutti i metodi/canali.</p>
        </Section>

        {/* 12. GEOGRAFIA */}
        <Section value="geo" title="Geografia di Vendibilità">
          <Field label="Regioni" hint={`${form.regioni_vendibilita.length}/20 — vuoto = tutta Italia`}>
            <div className="flex flex-wrap gap-2 mt-2">
              {(options.regioni_italia || []).map(r => (
                <button key={r} type="button" onClick={() => toggleMulti('regioni_vendibilita', r)}
                  className={`text-xs px-2 py-1 rounded border transition ${
                    form.regioni_vendibilita.includes(r)
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}>{r}</button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Province (sigle, separate da virgola)">
              <Input value={(form.province_vendibilita || []).join(', ')}
                onChange={(e) => updateField('province_vendibilita',
                  e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))}
                placeholder="MI, RM, NA" />
            </Field>
            <Field label="Comuni (separati da virgola)">
              <Input value={(form.comuni_vendibilita || []).join(', ')}
                onChange={(e) => updateField('comuni_vendibilita',
                  e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="Milano, Roma" />
            </Field>
          </div>
        </Section>

        {/* 13. CONDIZIONI OFFERTA */}
        <Section value="condizioni" title="Condizioni CTE">
          <RowEditor
            rows={form.condizioni_offerta}
            columns={[
              { key: 'nome', label: 'Nome', type: 'text', width: 'col-span-3' },
              { key: 'descrizione', label: 'Descrizione', type: 'text', width: 'col-span-6' },
              { key: 'limitante', label: 'Limitante', type: 'select',
                options: [{ v: 'true', l: 'Sì' }, { v: 'false', l: 'No' }], width: 'col-span-2' },
            ]}
            onAdd={() => addRow('condizioni_offerta', { nome: '', descrizione: '', limitante: 'false' })}
            onUpdate={(i, k, v) => updateRow('condizioni_offerta', i, k, v)}
            onRemove={(i) => removeRow('condizioni_offerta', i)} />
        </Section>

        {/* 14. TESTI AGGIUNTIVI */}
        <Section value="testi" title="Testi Aggiuntivi (per CTE / contratto)">
          <RowEditor
            rows={form.testi_aggiuntivi}
            columns={[
              { key: 'servizio', label: 'Servizio', type: 'text', width: 'col-span-3' },
              { key: 'campo', label: 'Campo', type: 'text', width: 'col-span-3' },
              { key: 'valore', label: 'Valore', type: 'text', width: 'col-span-5' },
            ]}
            onAdd={() => addRow('testi_aggiuntivi', { servizio: '', campo: '', valore: '' })}
            onUpdate={(i, k, v) => updateRow('testi_aggiuntivi', i, k, v)}
            onRemove={(i) => removeRow('testi_aggiuntivi', i)} />
        </Section>

        {/* 15. DISPACCIAMENTO + CAPACITY */}
        <Section value="dispatch" title="Dispacciamento e Capacity Market">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dispacciamento">
              <Select value={form.tipo_dispacciamento} onValueChange={(v) => updateField('tipo_dispacciamento', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options.dispacciamento || []).map(d => <SelectItem key={d} value={d}>{d.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Capacity Market">
              <Select value={form.capacity_market} onValueChange={(v) => updateField('capacity_market', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(options.capacity_market || []).map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        {/* 16. MARGINE COMMERCIALE (interno) */}
        <Section value="margine" title="Margine Commerciale (interno)" badge="Interno">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Margine atteso (€/MWh)">
              <Input type="number" step="0.01" value={form.margine_commerciale?.atteso_eur_mwh || 0}
                     onChange={(e) => updateMargine('atteso_eur_mwh', parseFloat(e.target.value) || 0)} />
            </Field>
            <Field label="Margine minimo (€/MWh)">
              <Input type="number" step="0.01" value={form.margine_commerciale?.minimo_eur_mwh || 0}
                     onChange={(e) => updateMargine('minimo_eur_mwh', parseFloat(e.target.value) || 0)} />
            </Field>
            <Field label="Note">
              <Textarea rows={1} value={form.margine_commerciale?.note || ''}
                        onChange={(e) => updateMargine('note', e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* 17. TEMPLATE CTE */}
        <Section value="template" title="Template CTE">
          <Field label="Template HTML da utilizzare">
            <Select value={form.cte_template} onValueChange={(v) => updateField('cte_template', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.filename} value={t.filename}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <p className="text-xs text-slate-400 mt-2">Suggerimento: usa "Upload PDF" nella toolbar per sostituire la generazione automatica con un PDF già impaginato.</p>
        </Section>

        {/* 18. STILE AGGIUNTIVO */}
        <Section value="stile" title="Stile Aggiuntivo (CSS/HTML inline per CTE)">
          <p className="text-xs text-slate-500 mb-2">Personalizzazioni grafiche da iniettare nel CTE generato</p>
          <RowEditor
            rows={form.stile_aggiuntivo}
            columns={[
              { key: 'servizio', label: 'Servizio', type: 'text', width: 'col-span-3' },
              { key: 'campo', label: 'Campo', type: 'text', width: 'col-span-3' },
              { key: 'valore', label: 'Valore (CSS/HTML)', type: 'text', width: 'col-span-5' },
            ]}
            onAdd={() => addRow('stile_aggiuntivo', { servizio: '', campo: '', valore: '' })}
            onUpdate={(i, k, v) => updateRow('stile_aggiuntivo', i, k, v)}
            onRemove={(i) => removeRow('stile_aggiuntivo', i)} />
        </Section>

        {/* 19. ALLEGATI */}
        <Section value="allegati" title={`Allegati (${(form.allegati || []).length})`}>
          {!isNew ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" id="att-upload"
                       onChange={(e) => e.target.files[0] && uploadAttachment(e.target.files[0], '')}
                       className="hidden" data-testid="attachment-input" />
                <Button asChild size="sm" variant="outline" className="text-green-700 border-green-200">
                  <label htmlFor="att-upload" className="cursor-pointer">
                    <Plus size={14} className="mr-1" /> Carica allegato
                  </label>
                </Button>
                <span className="text-xs text-slate-500">PDF / PNG / JPG / DOCX / XLSX (max 20MB)</span>
              </div>
              {(form.allegati || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nessun allegato</p>}
              <div className="space-y-2">
                {(form.allegati || []).sort((a, b) => (a.ordinamento || 0) - (b.ordinamento || 0)).map(a => (
                  <div key={a.id} className="flex items-center justify-between border rounded p-2 hover:bg-slate-50">
                    <div className="flex items-center gap-2">
                      <Paperclip size={14} className="text-slate-400" />
                      <span className="text-sm font-medium">{a.label || a.filename}</span>
                      <span className="text-xs text-slate-400">{a.filename}</span>
                      <span className="text-xs text-slate-400">{(a.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <div className="flex gap-1">
                      <a href={`${API}/api/offers/${offerId}/attachments/${a.id}`} target="_blank" rel="noreferrer"
                         className="text-blue-600 hover:bg-blue-50 p-1 rounded">
                        <DownloadSimple size={16} />
                      </a>
                      <button onClick={() => removeAttachment(a.id)}
                              className="text-red-600 hover:bg-red-50 p-1 rounded">
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Salva la CTE per caricare allegati.</p>
          )}
        </Section>

        {/* 20. STORICO MODIFICHE */}
        <Section value="storico" title={`Storico modifiche (${(form.history || []).length})`}>
          {(form.history || []).length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-2">Nessun evento registrato</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {(form.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs border-b py-1">
                  <ClockCounterClockwise size={12} className="text-slate-400" />
                  <Badge variant="outline" className="text-[10px]">{h.event}</Badge>
                  <span className="text-slate-600">{h.by}</span>
                  <span className="text-slate-400 ml-auto">{(h.at || '').slice(0, 19).replace('T', ' ')}</span>
                  {h.details && <span className="text-slate-500 italic">— {h.details}</span>}
                </div>
              ))}
            </div>
          )}
        </Section>

      </Accordion>

      <div className="mt-8 mb-12 flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/offers')}>Annulla</Button>
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          <FloppyDisk size={16} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva CTE'}
        </Button>
      </div>

      {/* UPLOAD CTE DIALOG */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carica CTE custom (PDF)</DialogTitle>
            <DialogDescription>
              Sostituisce la generazione automatica con un PDF già impaginato. Max 10MB.
            </DialogDescription>
          </DialogHeader>
          <input type="file" accept=".pdf"
                 onChange={(e) => setUploadCteFile(e.target.files[0])}
                 className="text-sm" data-testid="cte-file-input" />
          {uploadCteFile && (
            <p className="text-sm text-slate-600">Selezionato: <strong>{uploadCteFile.name}</strong> ({(uploadCteFile.size / 1024).toFixed(1)} KB)</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadCteFile(null); }}>Annulla</Button>
            <Button onClick={async () => {
              if (uploadCteFile) {
                await uploadCustomCte(uploadCteFile);
                setShowUploadDialog(false);
                setUploadCteFile(null);
              }
            }} disabled={!uploadCteFile} className="bg-purple-600 hover:bg-purple-700"
               data-testid="confirm-upload-cte">
              <UploadSimple size={14} className="mr-1" /> Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INCIDENZA MODAL */}
      <Dialog open={!!incidenza} onOpenChange={(o) => !o && setIncidenza(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChartPieSlice size={20} className="text-green-600" />
              Incidenza percentuale costi
            </DialogTitle>
            <DialogDescription>
              Stima sui consumi di riferimento (3.000 kWh/anno).
            </DialogDescription>
          </DialogHeader>
          {incidenza && (
            <div className="space-y-3" data-testid="incidenza-modal">
              <div className="text-center bg-orange-50 rounded p-3">
                <div className="text-xs text-slate-500">Totale stimato anno</div>
                <div className="text-3xl font-bold text-orange-600">€ {incidenza.totale_anno_eur.toFixed(2)}</div>
              </div>
              <div className="space-y-2">
                {incidenza.breakdown.map((b, i) => {
                  const w = Math.max(Math.abs(b.pct), 2);
                  const isNeg = b.pct < 0;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{b.voce}</span>
                        <span className={isNeg ? 'text-green-600' : 'text-slate-700'}>
                          €&nbsp;{b.eur_anno.toFixed(2)} <span className="text-slate-400">({b.pct}%)</span>
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded">
                        <div className={`h-2 rounded ${isNeg ? 'bg-green-500' : 'bg-orange-500'}`}
                             style={{ width: `${Math.min(w, 100)}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 italic">{incidenza.note}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// COMPONENTI INTERNI
// ============================================================

function Section({ value, title, children, badge }) {
  return (
    <AccordionItem value={value} className="border rounded-lg bg-white px-4">
      <AccordionTrigger className="hover:no-underline" data-testid={`section-${value}`}>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900">{title}</span>
          {badge && <Badge variant="outline" className="text-xs">{badge}</Badge>}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-4">{children}</AccordionContent>
    </AccordionItem>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function ToggleField({ label, v, onChange }) {
  return (
    <label className="flex items-center justify-between bg-slate-50 rounded p-2 cursor-pointer hover:bg-slate-100">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={v} onCheckedChange={onChange} />
    </label>
  );
}

function MultiSelect({ label, all, selected, onToggle }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="flex flex-wrap gap-1 mt-1">
        {all.map(item => (
          <button key={item} type="button" onClick={() => onToggle(item)}
            className={`text-xs px-2 py-1 rounded border transition ${
              selected.includes(item)
                ? 'bg-orange-500 text-white border-orange-500'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}>{item}</button>
        ))}
      </div>
    </div>
  );
}

function RowEditor({ rows, columns, onAdd, onUpdate, onRemove }) {
  return (
    <div>
      <div className="flex justify-end mb-2">
        <Button size="sm" variant="outline" onClick={onAdd} className="text-green-700 border-green-200">
          <Plus size={14} className="mr-1" /> Aggiungi
        </Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Nessun elemento</p>}
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            {columns.map(col => (
              <div key={col.key} className={col.width || 'col-span-3'}>
                {col.type === 'select' ? (
                  <Select value={String(row[col.key] || '')} onValueChange={(v) => onUpdate(i, col.key, v)}>
                    <SelectTrigger><SelectValue placeholder={col.label} /></SelectTrigger>
                    <SelectContent>
                      {col.options.map(opt => <SelectItem key={opt.v} value={opt.v}>{opt.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type={col.type === 'number' ? 'number' : 'text'} placeholder={col.label}
                         value={row[col.key] || ''}
                         onChange={(e) => onUpdate(i, col.key, col.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)} />
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => onRemove(i)} className="col-span-1 text-red-600">
              <X size={14} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
